import { DocumentReference } from '@firebase/firestore-types';
import { useFeatures } from '@paralleldrive/react-feature-toggles';
import { getDownloadURL, ref } from 'firebase/storage';
import _ from 'lodash';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useFirestore } from 'react-redux-firebase';
import { useNavigate } from 'react-router-dom';
import { getStepsByBranchByMarket } from '..';
import { Button } from '../../../components/Button';
import { useCountryMarketState } from '../../../controllers/CountryMarketController/CountryMarketState';
import { storage } from '../../../firebase';
import {
  GTMBaseContentViewObject,
  GTMEventData,
} from '../../../hooks/GTMUtils';
import { useAppRoutes } from '../../../hooks/useAppRoutes';
import { useAuth } from '../../../hooks/useAuth';
import { getCountryMarketStringByConfig } from '../../../hooks/useCountryMarketHelpers';
import { useGTM } from '../../../hooks/useGTM';
import {
  getTaxonomyDisplayName,
  isTaxonomyWithKeyMultiselect,
} from '../../../lib/briefing';
import { capitalise, getTodaysDateInFormat } from '../../../lib/utils';
import {
  AttachedFile,
  BriefingEmailData,
  BriefingState,
  MandatoryFieldDefinition,
  Price,
  PriceType,
  Step,
  TaxonomyWithKey,
  TrackingSchema,
} from '../../../types';
import { useBriefingState } from '../BriefingState';
import { displayOption } from '../BriefingUtils';
import { useCustomerState } from '../CustomerState';

interface BriefingFooterProps {
  step: Step;
  mandatoryFields: MandatoryFieldDefinition;
  isValid?: boolean;
}

const generateSignedDownloadUrl = (
  file: AttachedFile,
  authUid: string,
): Promise<string> =>
  getDownloadURL(
    ref(storage, `userUploads/${authUid}/${file.id}/${file.adjustedName}`),
  );

function removeUndefinedProperties<T>(obj: T): T {
  if (typeof obj !== 'object') return obj;
  return JSON.parse(JSON.stringify(obj));
}

export const isIncomplete = (
  currentRequiredFields: (keyof BriefingState)[],
  isValid: boolean,
  briefingState: BriefingState,
): boolean =>
  !isValid ||
  currentRequiredFields?.some(
    (field) =>
      briefingState[field] === undefined ||
      briefingState[field] === null ||
      (briefingState[field] as string | unknown[]).length === 0,
  );

const getFormulationBranchName = (
  branch: 'create' | 'enhance' | 'modify' | 'fix',
) => {
  switch (branch) {
    case 'create':
      return 'Created';
    case 'enhance':
      return 'Enhanced';
    case 'modify':
      return 'Modified';
    case 'fix':
      return 'Fixed';
  }
};

export const BriefingFooter: FC<BriefingFooterProps> = ({
  step,
  mandatoryFields,
  isValid = true,
}: BriefingFooterProps) => {
  const navigate = useNavigate();

  const [briefingState, { setMaxStepIndexAlreadyReached }] = useBriefingState();

  const {
    branch,
    steps,
    productName,
    maxStepIndexAlreadyReached,
    ...marketSegmentSpecificBriefingState
  } = briefingState;

  const { stepSuccessRoute, briefingRoute } = useAppRoutes();

  const dispatch = useDispatch();

  const [{ user, displayUser, auth, customer }] = useAuth();
  const [{ cpCustomer, customerUser, cpCustomerUser }] = useCustomerState();
  const [{ countryMarketConfig }] = useCountryMarketState();
  const featureNames = useFeatures();
  const firestore = useFirestore();
  const { t, i18n } = useTranslation();
  const { updateDataLayer } = useGTM();

  const stepIndex = useMemo(() => steps.indexOf(step), [step, steps]);

  const onNext = useCallback(() => {
    if (stepIndex > -1 && stepIndex < steps.length - 1) {
      navigate(
        `/${countryMarketConfig?.eLabCountrySlug}/${
          countryMarketConfig?.eLabMarketSlug
        }/briefing/${branch}/${steps[stepIndex + 1]}`,
      );
    }
  }, [branch, steps, stepIndex, navigate, countryMarketConfig]);

  // This needs to be adapted for each new step that is add to the briefing flows.
  // It needs to handle eeach field and push a { key, value } object to the data array that should end up in emails
  const generateMarketSegmentSpecificBriefingContent = useCallback(
    async (
      stateData: Omit<
        BriefingState,
        'steps' | 'branch' | 'maxStepIndexAlreadyReached' | 'productName'
      >,
    ): Promise<
      { key: string; value: string | { key: string; value: string }[] }[]
    > => {
      const data: {
        key: string;
        value: string | { key: string; value: string }[];
      }[] = [];

      for (const step of steps) {
        switch (step) {
          case 'modification-issue':
            data.push({
              key: `${t('topics:modification-issue:name')}`,
              value: stateData.modifyBriefingIssue
                ? stateData.modifyBriefingIssue
                : '-',
            });
            break;

          case 'target-consumer':
            if (stateData.targetConsumerAge) {
              data.push({
                key: `${t('topics:target-consumer:age')}`,
                value: stateData.targetConsumerAge.join(', '),
              });
            }
            if (stateData.targetConsumerGender) {
              data.push({
                key: `${t('topics:target-consumer:gender')}`,
                value: stateData.targetConsumerGender,
              });
            }
            break;

          case 'application': {
            const value = stateData.productApplications
              .map(
                (app, index) =>
                  `<strong>${t(
                    `topics:application:levels:${index}:selected`,
                  )}</strong>: ${getTaxonomyDisplayName(
                    app,
                    stateData.productApplicationsOther,
                    t('labels:other'),
                  )}`,
              )
              .join('<br />');

            data.push({
              key: t('topics:application:name'),
              value,
            });
            break;
          }

          case 'active-ingredients': {
            if (stateData.activeIngredients) {
              const activeIngredientsValue = _.values(
                stateData.activeIngredients,
              )
                .map(
                  (ingredient) =>
                    `${ingredient.ingredient?.name}, ${ingredient.concentration} g/L`,
                )
                .join('<br />');

              data.push({
                key: t('topics:active-ingredients:name'),
                value: activeIngredientsValue,
              });
            }
            break;
          }

          case 'active-function': {
            if (stateData.activeFunctions) {
              const activeFunctionValue = stateData.activeFunctions
                .filter((func) => func)
                .join(', ');

              data.push({
                key: t('topics:active-function:heading:review'),
                value: activeFunctionValue,
              });
            }
            break;
          }

          case 'formulation-attributes':
            if (stateData.texture) {
              data.push({
                key: `${t('topics:formulation-attributes:texture')}`,
                value: displayOption(stateData.texture, stateData.textureOther),
              });
            }

            if (stateData.storage) {
              data.push({
                key: `${t('topics:formulation-attributes:storage')}`,
                value: displayOption(stateData.storage, stateData.storageOther),
              });
            }

            if (stateData.formulationType) {
              data.push({
                key: `${t('topics:formulation-attributes:formulationType')}`,
                value: displayOption(
                  stateData.formulationType,
                  stateData.formulationTypeOther,
                ),
              });
            }

            if (stateData.productForm) {
              data.push({
                key: `${t('topics:formulation-attributes:productForm')}`,
                value: displayOption(
                  stateData.productForm,
                  stateData.productFormOther,
                ),
              });
            }

            if (stateData.productMarketingClaims) {
              data.push({
                key: `${t(
                  'topics:formulation-attributes:productMarketingClaims',
                )}`,
                value: displayOption(
                  stateData.productMarketingClaims,
                  stateData.productMarketingClaimsOther,
                ),
              });
            }
            break;

          case 'packaging':
            if (stateData.packaging) {
              data.push({
                key: `${t('topics:packaging:name')}`,
                value: displayOption(
                  stateData.packaging,
                  stateData.packagingOther,
                ),
              });
            }
            break;

          case 'restrictions':
            data.push({
              key: `${t('topics:restrictions:requirements')}`,
              value: [
                {
                  key: `${t('topics:restrictions:must')}`,
                  value: displayOption(
                    stateData.requirementsMust,
                    stateData.requirementsMustOther,
                    '-',
                  ),
                },
                {
                  key: `${t('topics:restrictions:can')}`,
                  value: displayOption(
                    stateData.requirementsCan,
                    stateData.requirementsCanOther,
                    '-',
                  ),
                },
              ],
            });

            data.push({
              key: `${t('topics:restrictions:restrictions')}`,
              value: [
                {
                  key: `${t('topics:restrictions:must')}`,
                  value: displayOption(
                    stateData.restrictionsMust,
                    stateData.restrictionsMustOther,
                    '-',
                  ),
                },
                {
                  key: `${t('topics:restrictions:can')}`,
                  value: displayOption(
                    stateData.restrictionsCan,
                    stateData.restrictionsCanOther,
                    '-',
                  ),
                },
              ],
            });

            break;

          case 'requirements': {
            const phValues = _.values(stateData.requirementsPHRange);
            const phRangeContent = phValues.length
              ? `${phValues[0]} - ${phValues[1]}`
              : null;

            const allRequirements = [
              {
                key: 'topics:restrictions:must',
                value: displayOption(
                  stateData.requirementsMust,
                  stateData.requirementsMustOther,
                  '-',
                ),
              },
              {
                key: 'topics:restrictions:can',
                value: displayOption(stateData.requirementsCan, '-'),
              },
              {
                key: 'topics:requirements:aggressiveComponents:label',
                value: stateData.requirementsAggressiveComponents,
              },
              {
                key: 'topics:requirements:lightStability:label',
                value: stateData.requirementsLightStability,
              },
              {
                key: 'topics:requirements:applicationConditionDetails:label',
                value: stateData.requirementsApplicationConditionDetails,
              },
              {
                key: 'topics:requirements:pHRange:label',
                value: phRangeContent,
              },
            ];

            data.push({
              key: `${t('topics:requirements:name')}`,
              value: allRequirements
                .filter(({ value }) => value !== null)
                .map(
                  ({ key, value }) =>
                    `<strong>${t(key)}</strong>: ${value || t('-')}`,
                )
                .join('<br/>'),
            });

            break;
          }

          case 'colour': {
            const allColourFields = [
              {
                key: 'topics:colour:colourShades',
                value:
                  typeof stateData.colourShade === 'string'
                    ? stateData.colourShade
                    : displayOption(stateData.colourShade, '-'),
              },
              {
                key: 'topics:colour:colourSamples:label',
                value: stateData.colourSamples,
              },
              {
                key: 'topics:colour:requiredTime:label',
                value: stateData.colourRequiredTime,
              },
              {
                key: 'topics:colour:labSupport:label',
                value: stateData.colourLabSupport,
              },
            ];

            data.push({
              key: `${t('topics:colour:name')}`,
              value: allColourFields
                .filter(({ value }) => value !== null)
                .map(
                  ({ key, value }) =>
                    `<strong>${t(key)}</strong>: ${value || t('-')}`,
                )
                .join('<br/>'),
            });

            break;
          }

          case 'fragrance': {
            const allFragranceFields = [
              {
                key: 'topics:fragrance:colourOrConcept',
                value: displayOption(stateData.fragranceConcept, '-'),
              },
              {
                key: 'topics:fragrance:direction',
                value: displayOption(stateData.fragranceDirection, '-'),
              },
              {
                key: 'topics:fragrance:mustComply',
                value: displayOption(
                  stateData.fragranceMust,
                  stateData.fragranceMustOther,
                  '-',
                ),
              },
              {
                key: 'topics:fragrance:CMR',
                value: stateData.fragranceCMR,
              },
              {
                key: 'topics:fragrance:maxAllergensOnLabel',
                value: stateData.fragranceMaxAmountAllergens,
              },
              {
                key: 'topics:fragrance:requestedApplicationDosage',
                value: stateData.fragranceRequestedApplicationDosage,
              },
              {
                key: 'topics:fragrance:fragranceSamples',
                value: stateData.fragranceSamples,
              },
              {
                key: 'topics:fragrance:requiredSampleTime:label',
                value: stateData.fragranceRequiredSampleTime,
              },
              {
                key: 'topics:fragrance:largerFragranceSize:label',
                value: stateData.fragranceLargerSample,
              },
            ];

            data.push({
              key: `${t('topics:fragrance:name')}`,
              value: allFragranceFields
                .filter(({ value }) => value !== null)
                .map(
                  ({ key, value }) =>
                    `<strong>${t(key)}</strong>: ${value || t('-')}`,
                )
                .join('<br/>'),
            });

            break;
          }

          case 'price': {
            const displayPrice = (priceValue?: Price) =>
              `${priceValue?.min || 0} - ${priceValue?.max || 'n.a.'} ${
                countryMarketConfig?.currency
              } / ${countryMarketConfig?.weightUnit}`;

            const { prices } = stateData;

            const priceKeys = _.keys(prices);
            const priceValues = _.values(prices);
            const priceEntries = _.entries(prices);

            const content: { heading?: string; content: string }[] =
              priceKeys.length === 1 && priceKeys[0] === 'base'
                ? [
                    {
                      content: displayPrice(priceValues[0]),
                    },
                  ]
                : priceEntries.map(([key, priceValue]) => ({
                    heading: t([
                      `topics:price:heading:${key}`,
                      `topics:price:heading:general`,
                    ]),
                    content: displayPrice(priceValue),
                  }));

            data.push({
              key: `${t('topics:price:name')}`,
              value: content
                .map(
                  ({ heading, content }) =>
                    `${heading && `<strong>${heading}</strong>: `}${
                      content || t('placeholders:notApplicable')
                    }`,
                )
                .join('<br/>'),
            }); // TODO: needs to be in the user settings
            break;
          }

          case 'product-details': {
            const allDetails = [
              {
                key: 'topics:product-details:finishedProductSamples:heading',
                value: stateData.finishedProductSamples,
              },
              {
                key: 'topics:fragrance:requiredSampleTime:label',
                value: stateData.requiredSampleTime,
              },
              {
                key: 'topics:product-details:finishedProductPotential',
                value: stateData.finishedProductPotential,
              },
              {
                key: 'topics:product-details:projectDeadline',
                value: stateData.projectDeadline,
              },
            ];
            data.push({
              key: `${t('topics:product-details:name')}`,
              value: allDetails
                .filter(({ value }) => value !== null)
                .map(
                  ({ key, value }) =>
                    `<strong>${t(key)}</strong>: ${value || t('-')}`,
                )
                .join('<br/>'),
            });

            break;
          }

          case 'problem':
            if (stateData.problem) {
              data.push({
                key: `${t(`topics:problem:${branch}:name`)}`,
                value: stateData.problem,
              });
            }
            break;

          case 'processing-capabilities': {
            const possibleAttributes = [
              {
                key: 'bakeStability',
                value: stateData.bakeStability,
              },
              {
                key: 'bakingTime',
                value: stateData.bakingTime,
              },
              {
                key: 'bakingTemperature',
                value: stateData.bakingTemperature,
              },
              {
                key: 'mixingStepDetails',
                value: stateData.mixingStepDetails,
              },
              {
                key: 'productPh',
                value: stateData.productPh,
              },
              {
                key: 'brix',
                value: stateData.brix,
              },
              {
                key: 'fillingTime',
                value: stateData.fillingTime,
              },
              {
                key: 'fillingTemperature',
                value: stateData.fillingTemperature,
              },
              {
                key: 'processingCapabilitiesPackagingDetails',
                value: stateData.processingCapabilitiesPackagingDetails,
              },
              {
                key: 'foodSafetyDetails',
                value: stateData.foodSafetyDetails,
              },
              {
                key: 'processingHotCold',
                value: stateData.processingHotCold,
              },
              {
                key: 'homogenisation',
                value: stateData.homogenisation,
              },
              {
                key: 'otherProcessingCapabilities',
                value: stateData.otherProcessingCapabilities,
              },
            ];

            data.push({
              key: `${t('topics:processing-capabilities:name')}`,
              value: possibleAttributes
                .filter(({ value }) => value !== null)
                .map(
                  ({ key, value }) =>
                    `<strong>${t(
                      `topics:processing-capabilities:${key}`,
                    )}</strong>: ${value || t('placeholders:notApplicable')}`,
                )
                .join('<br/>'),
            });

            break;
          }

          case 'ingredients': {
            const { rawMaterial, ingredientsFiles, azelisRawMaterials } =
              stateData;

            if (azelisRawMaterials.length > 0) {
              data.push({
                key: `${t('topics:ingredients:azelisRawMaterials')}`,
                value: azelisRawMaterials.map(({ name }) => name).join('<br/>'),
              });
            }
            if (rawMaterial) {
              data.push({
                key: `${t('topics:ingredients:rawMaterial')}`,
                value: rawMaterial,
              });
            }
            if (ingredientsFiles) {
              const filesWithLinks: string[] = [];
              for await (const file of ingredientsFiles) {
                filesWithLinks.push(
                  `<a href="${await generateSignedDownloadUrl(
                    file,
                    auth.uid,
                  )}" target="_blank">${file.adjustedName}</a>`,
                );
              }

              data.push({
                key: `${t('topics:ingredients:rawMaterialFiles')}`,
                value: filesWithLinks.join(', '),
              });
            }
            break;
          }
          case 'additional-info':
            if (stateData.additionalInfo) {
              data.push({
                key: `${t('topics:additional-info:additionalInfo')}`,
                value: stateData.additionalInfo,
              });
            }

            if (stateData.additionalInfoFiles) {
              const filesWithLinks: string[] = [];
              for await (const file of stateData.additionalInfoFiles) {
                filesWithLinks.push(
                  `<a href="${await generateSignedDownloadUrl(
                    file,
                    auth.uid,
                  )}" target="_blank">${file.adjustedName}</a>`,
                );
              }

              data.push({
                key: `${t('topics:additional-info:additionalInfoFiles')}`,
                value: filesWithLinks.join(', '),
              });
            }
            break;
        }
      }

      return data;
    },
    [t, auth.uid, branch, steps, countryMarketConfig],
  );

  type GTMBriefingResultData = {
    creationDate: string;
    name: string | null;
    appl?: {
      mainCat: string | TaxonomyWithKey;
      subCat: string | TaxonomyWithKey;
      characteristics: string[];
    };
    attr?: {
      marketClaims: (string | number | null)[];
      storage: string;
    };
    cost?: {
      key: PriceType;
      min: string | number;
      max: string | number;
    }[];
  };

  const collectGTMLoggingData =
    useCallback((): GTMEventData<GTMBriefingResultData> => {
      const applications = [
        ...marketSegmentSpecificBriefingState.productApplications,
      ];

      let fallbackMainCat;
      let fallbackSubCat;
      const other = marketSegmentSpecificBriefingState.productApplicationsOther;
      if (applications.length) {
        fallbackMainCat =
          applications[0].key === 'other'
            ? `Other (${other})`
            : applications[0].key;
      }

      if (applications.length > 1) {
        fallbackSubCat =
          applications[1].key === 'other'
            ? `Other (${other})`
            : applications[1].key;
      }

      const characteristics: string[] = [];
      if (applications[2] && isTaxonomyWithKeyMultiselect(applications[2])) {
        characteristics.push(
          ...Object.values(applications[2]).map(({ key }) => key),
        );

        if (characteristics.includes('other')) {
          characteristics.push(`Other (${other})`);
          characteristics.splice(characteristics.indexOf('other'), 1);
        }
      }

      const mainCat = fallbackMainCat ? fallbackMainCat : '-';
      const subCat = fallbackSubCat ? fallbackSubCat : '-';

      const result: GTMEventData<GTMBriefingResultData> = {
        event: `Formulation ${branch && getFormulationBranchName(branch)}`,
        schema: TrackingSchema.formulation,
        data: {
          creationDate: getTodaysDateInFormat('yyyy-mm-dd'),
          name: productName,
          appl: {
            mainCat,
            subCat,
            characteristics,
          },
          attr: {
            marketClaims: [
              ...(marketSegmentSpecificBriefingState.productMarketingClaims?.map(
                (productMarketingClaim) => productMarketingClaim.value,
              ) || []),
              marketSegmentSpecificBriefingState.productMarketingClaimsOther,
            ].filter(
              (productMarketingClaimOther) => productMarketingClaimOther,
            ),
            storage: marketSegmentSpecificBriefingState.storage
              ? `${marketSegmentSpecificBriefingState.storage.value} ${
                  marketSegmentSpecificBriefingState.storage.value === 'other'
                    ? `(${marketSegmentSpecificBriefingState.storageOther})`
                    : ''
                }`.trim()
              : 'No storage selected',
          },
          cost: _.entries(marketSegmentSpecificBriefingState.prices).map(
            ([key, price]) => ({
              key: key as PriceType,
              min: price?.min || 'No min cost selected',
              max: price?.max || 'No max cost selected',
            }),
          ),
        },
      };

      switch (branch) {
        case 'enhance':
        case 'modify':
          delete result.data.cost;
          break;
        case 'fix':
          delete result.data.cost;
          delete result.data.attr;
          break;
      }
      return result;
    }, [branch, marketSegmentSpecificBriefingState, productName]);

  const onSendBriefing = useCallback(async () => {
    if (!branch || !countryMarketConfig || !user || !displayUser) return;

    updateDataLayer({
      ...GTMBaseContentViewObject,
      data: {
        category: capitalise(branch),
        type: `Step ${briefingState.steps.length} / ${briefingState.steps.length} Success`,
        content: 'Success Page',
      },
    });

    const displayUserName = `${displayUser.first_name} ${displayUser.last_name}`;
    const address = displayUser.email ?? (auth.email as string);
    const employeeCreatorName =
      customerUser && `${user.first_name} ${user.last_name}`;

    const byDisplayName = `${
      employeeCreatorName
        ? t('emails:lab:byFor', {
            byMe: employeeCreatorName,
            forMe: displayUserName,
          })
        : displayUserName
    } (${displayUser.customer_name ?? cpCustomer?.name})`;

    const translatedBranch = t(`emails:auxiliary:branch:${branch}`) || branch;

    const branchFormulationPrefix =
      branch === 'create'
        ? t('emails:customer:html:newFormulationPrefix')
        : t('emails:customer:html:existingFormulationPrefix');
    const intro = !employeeCreatorName
      ? t('emails:customer:html:introNotCreatedByEmployee', {
          branch: translatedBranch,
          branchFormulationPrefix,
        })
      : t('emails:customer:html:introCreatedByEmployee', {
          employeeCreatorName,
          branch: translatedBranch,
          branchFormulationPrefix,
        });

    // Locale is hard-coded for now, until we have the data in the cookie
    const customerLocale = i18n.language;
    const customerPortalMarketSegmentId =
      countryMarketConfig.customerPortalMarketSegmentId;

    const briefingEmailData: BriefingEmailData = {
      to: {
        customerName: displayUserName,
        employeeCreatorName,
        address,
        ccAddress: user.azelis_employee
          ? (cpCustomer?.am_email as string)
          : user.am_email || '',
      },
      customerUserId: displayUser.uid ?? auth.uid,
      countryDisplayName: t(
        `labels:countries:${countryMarketConfig.countryCluster}`,
      ),
      marketSegmentDisplayName: countryMarketConfig.marketDisplayName,
      configCountryMarketName:
        getCountryMarketStringByConfig(countryMarketConfig),
      customerPortalMarketSegmentId,
      intro,
      customerLocale,
      additionalText: {
        customer: {
          subject: t('emails:customer:subject'),
          html: {
            introNotCreatedByEmployee: t(
              'emails:customer:html:introNotCreatedByEmployee',
            ),
            introCreatedByEmployee: t(
              'emails:customer:html:introCreatedByEmployee',
            ),
            newFormulationPrefix: t(
              'emails:customer:html:newFormulationPrefix',
            ),
            existingFormulationPrefix: t(
              'emails:customer:html:existingFormulationPrefix',
            ),
            heading: t('emails:customer:html:heading'),
            line1: t('emails:customer:html:line1', {
              customerName: displayUserName,
            }),
            line2: t('emails:customer:html:line2'),
            line3: t('emails:customer:html:line3'),
            referenceId: t('emails:customer:html:referenceId'),
            projectName: t('emails:customer:html:projectName'),
            marketSegment: t('emails:customer:html:marketSegment'),
            country: t('emails:customer:html:country'),
            thankYou: t('emails:customer:html:thankYou'),
            inspirationSuggestion: t(
              'emails:customer:html:inspirationSuggestion',
              {
                inspirationLink: `<a target="_blank" href="${
                  process.env.REACT_APP_CP_BASEURL
                }/${customerLocale}/${customerPortalMarketSegmentId}/inspiration">${t(
                  'emails:customer:html:inspirationLinkText',
                )}</a>`,
              },
            ),
            theAzelisTeam: t('emails:customer:html:theAzelisTeam'),
            copyright: t('emails:customer:html:copyright', {
              year: new Date().getFullYear(),
            }),
          },
        },
        lab: {
          subject: t('emails:lab:subject', { byDisplayName }),
          html: {
            line1: t('emails:lab:html:line1', {
              branch: translatedBranch,
              branchFormulationPrefix,
              byDisplayName,
            }),
            referenceId: t('emails:lab:html:referenceId'),
            createdAt: t('emails:lab:html:createdAt'),
            projectName: t('emails:lab:html:projectName'),
            pleaseContact: t('emails:lab:html:pleaseContact', {
              mailToLink: `<a href="mailto:${address}?subject=Azelis%20e-Lab%20formulation%20briefing:%20${
                productName || t('emails:lab:html:unnamedProject')
              }">${displayUserName}</a>`,
            }),
            yourAzelisTeam: t('emails:lab:html:yourAzelisTeam'),
          },
        },
      },
      data: await generateMarketSegmentSpecificBriefingContent(
        marketSegmentSpecificBriefingState,
      ),
    };

    let briefingData = {
      amEmail: displayUser.am_email ?? cpCustomer?.am_email,
      status: 'sent',
      branch,
      createdBy: {
        uid: auth.uid,
        email: auth.email,
        first_name: user.first_name,
        last_name: user.last_name,
      },
      ownedBy: {
        uid: displayUser.uid ?? auth.uid,
        email: displayUser.email ?? auth.email,
        first_name: displayUser.first_name,
        last_name: displayUser.last_name,
      },
      country: countryMarketConfig.eLabCountrySlug,
      crmTwoAlphaCountryAbbreviation:
        countryMarketConfig.crmTwoAlphaCountryAbbreviation,
      crmThreeAlphaCountryAbbreviation:
        countryMarketConfig.datalakeCountryAbbreviation,
      crmMarketSegment: countryMarketConfig.crmMarketSegment,
      userGUIDForCRM: displayUser.GUID,
      invoiceGUIDForCRM: cpCustomerUser?.invoice_guid || displayUser?.invoice_guid,
      marketSegment: countryMarketConfig.eLabMarketSlug,
      name: productName,
      createdAt: new Date().toISOString(), // TODO: find a way to use firestore.Timestamp.now()
      targetConsumer: {
        age: marketSegmentSpecificBriefingState.targetConsumerAge,
        gender: marketSegmentSpecificBriefingState.targetConsumerGender,
      },
      applications: marketSegmentSpecificBriefingState.productApplications,
      applicationsOther:
        marketSegmentSpecificBriefingState.productApplicationsOther,
      activeIngredients: marketSegmentSpecificBriefingState.activeIngredients,
      activeFunctions: marketSegmentSpecificBriefingState.activeFunctions,
      productMarketingClaims:
        marketSegmentSpecificBriefingState.productMarketingClaims,
      productMarketingClaimsOther:
        marketSegmentSpecificBriefingState.productMarketingClaimsOther,
      formulationType: marketSegmentSpecificBriefingState.formulationType,
      formulationTypeOther:
        marketSegmentSpecificBriefingState.formulationTypeOther,
      texture: marketSegmentSpecificBriefingState.texture,
      textureOther: marketSegmentSpecificBriefingState.textureOther,
      storage: marketSegmentSpecificBriefingState.storage,
      storageOther: marketSegmentSpecificBriefingState.storageOther,
      productForm: marketSegmentSpecificBriefingState.productForm,
      productFormOther: marketSegmentSpecificBriefingState.productFormOther,
      restrictions: {
        restrictionsMust: marketSegmentSpecificBriefingState.restrictionsMust,
        restrictionsMustOther:
          marketSegmentSpecificBriefingState.restrictionsMustOther,
        restrictionsCan: marketSegmentSpecificBriefingState.restrictionsCan,
        restrictionsCanOther:
          marketSegmentSpecificBriefingState.restrictionsCanOther,
        requirementsMust: marketSegmentSpecificBriefingState.requirementsMust,
        requirementsMustOther:
          marketSegmentSpecificBriefingState.requirementsMustOther,
        requirementsCan: marketSegmentSpecificBriefingState.requirementsCan,
        requirementsCanOther:
          marketSegmentSpecificBriefingState.requirementsCanOther,
      },
      requirements: {
        requirementsMust: marketSegmentSpecificBriefingState.requirementsMust,
        requirementsMustOther:
          marketSegmentSpecificBriefingState.requirementsMustOther,
        requirementsCan: marketSegmentSpecificBriefingState.requirementsCan,
        requirementsApplicationConditionDetails:
          marketSegmentSpecificBriefingState.requirementsApplicationConditionDetails,
        requirementsPHRange:
          marketSegmentSpecificBriefingState.requirementsPHRange,
        requirementsAggressiveComponents:
          marketSegmentSpecificBriefingState.requirementsAggressiveComponents,
        requirementsLightStability:
          marketSegmentSpecificBriefingState.requirementsLightStability,
      },
      colour: {
        colourShade: marketSegmentSpecificBriefingState.colourShade,
        colourSamples: marketSegmentSpecificBriefingState.colourSamples,
        colourRequiredTime:
          marketSegmentSpecificBriefingState.colourRequiredTime,
        colourLabSupport: marketSegmentSpecificBriefingState.colourLabSupport,
      },
      fragrance: {
        fragranceConcept: marketSegmentSpecificBriefingState.fragranceConcept,
        fragranceDirection:
          marketSegmentSpecificBriefingState.fragranceDirection,
        fragranceMust: marketSegmentSpecificBriefingState.fragranceMust,
        fragranceMustOther:
          marketSegmentSpecificBriefingState.fragranceMustOther,
        fragranceCMR: marketSegmentSpecificBriefingState.fragranceCMR,
        fragranceMaxAmountAllergens:
          marketSegmentSpecificBriefingState.fragranceMaxAmountAllergens,
        fragranceRequestedApplicationDosage:
          marketSegmentSpecificBriefingState.fragranceRequestedApplicationDosage,
        fragranceSamples: marketSegmentSpecificBriefingState.fragranceSamples,
        fragranceRequiredSampleTime:
          marketSegmentSpecificBriefingState.fragranceRequiredSampleTime,
        fragranceLargerSample:
          marketSegmentSpecificBriefingState.fragranceLargerSample,
      },
      price: {
        prices: marketSegmentSpecificBriefingState.prices,
        /**
         * old data structure, used before Dec 2021
         *
         * needs to be taken care of as long as we have Briefings
         * with this data structure in Firestore
         */
        // min: marketSegmentSpecificBriefingState.estimatedPriceMin,
        // max: marketSegmentSpecificBriefingState.estimatedPriceMax,
        currency: countryMarketConfig.currency,
        baseUnit: countryMarketConfig.weightUnit,
      },
      productDetails: {
        finishedProductSamples:
          marketSegmentSpecificBriefingState.finishedProductSamples,
        requiredSampleTime:
          marketSegmentSpecificBriefingState.requiredSampleTime,
        finishedProductPotential:
          marketSegmentSpecificBriefingState.finishedProductPotential,
        projectDeadline: marketSegmentSpecificBriefingState.projectDeadline,
      },
      additionalInfo: marketSegmentSpecificBriefingState.additionalInfo,
      additionalInfoFiles:
        marketSegmentSpecificBriefingState.additionalInfoFiles &&
        marketSegmentSpecificBriefingState.additionalInfoFiles.length
          ? marketSegmentSpecificBriefingState.additionalInfoFiles
          : null,
      ingredientsFiles:
        marketSegmentSpecificBriefingState.ingredientsFiles &&
        marketSegmentSpecificBriefingState.ingredientsFiles.length
          ? marketSegmentSpecificBriefingState.ingredientsFiles
          : null,
      azelisRawMaterials: marketSegmentSpecificBriefingState.azelisRawMaterials,
      rawMaterial: marketSegmentSpecificBriefingState.rawMaterial,
      problem: marketSegmentSpecificBriefingState.problem,
      packaging: marketSegmentSpecificBriefingState.packaging,
      packagingOther: marketSegmentSpecificBriefingState.packagingOther,
      processingCapabilities: {
        bakeStability: marketSegmentSpecificBriefingState.bakeStability,
        bakingTime: marketSegmentSpecificBriefingState.bakingTime,
        bakingTemperature: marketSegmentSpecificBriefingState.bakingTemperature,
        fillingTime: marketSegmentSpecificBriefingState.fillingTime,
        fillingTemperature:
          marketSegmentSpecificBriefingState.fillingTemperature,
        mixingStepDetails: marketSegmentSpecificBriefingState.mixingStepDetails,
        productPh: marketSegmentSpecificBriefingState.productPh,
        brix: marketSegmentSpecificBriefingState.brix,
        otherProcessingCapabilities:
          marketSegmentSpecificBriefingState.otherProcessingCapabilities,
        processingCapabilitiesPackagingDetails:
          marketSegmentSpecificBriefingState.processingCapabilitiesPackagingDetails,
        foodSafetyDetails: marketSegmentSpecificBriefingState.foodSafetyDetails,
        processingHotCold: marketSegmentSpecificBriefingState.processingHotCold,
        homogenisation: marketSegmentSpecificBriefingState.homogenisation,
      },
      emailData: briefingEmailData,
    };

    /**
     * As a solution to AZEELAB-310: Briefing not saving in Firebase due to undefined values in briefingData structure
     */
    briefingData = removeUndefinedProperties(briefingData);

    let parentDoc: DocumentReference;

    // AZEELAB-95
    // Added false to prevent using Customers collection for storing briefings
    // Until we have figured out the logic of when a briefing should be a Company briefing, leave this disabled
    if (featureNames.includes('team-briefings') && displayUser?.erp_id) {
      // write Briefings to the customer, if a customer is referenced
      parentDoc = firestore.collection('customers').doc(displayUser.erp_id);
    } else {
      // otherwise store it with the user
      parentDoc = firestore
        .collection('users')
        .doc(displayUser?.uid || auth.uid);
    }

    parentDoc
      .collection('briefings')
      .add(briefingData)
      .then(() => {
        updateDataLayer(collectGTMLoggingData());
        navigate(stepSuccessRoute);
      });
  }, [
    branch,
    countryMarketConfig,
    user,
    displayUser,
    auth.email,
    auth.uid,
    customerUser,
    t,
    cpCustomer,
    i18n.language,
    productName,
    generateMarketSegmentSpecificBriefingContent,
    marketSegmentSpecificBriefingState,
    customer,
    featureNames,
    firestore,
    updateDataLayer,
    collectGTMLoggingData,
    navigate,
    stepSuccessRoute,
    briefingState.steps.length,
    cpCustomerUser,
  ]);

  useEffect(() => {
    if (!step || !branch) return;

    const currentStepIndex = getStepsByBranchByMarket(
      countryMarketConfig?.eLabMarketSlug,
      briefingState.modifyBriefingType,
    )[branch].indexOf(step);
    if (
      maxStepIndexAlreadyReached === null ||
      currentStepIndex > maxStepIndexAlreadyReached
    ) {
      dispatch(setMaxStepIndexAlreadyReached(currentStepIndex));
    }
  }, [
    step,
    branch,
    setMaxStepIndexAlreadyReached,
    countryMarketConfig,
    dispatch,
    maxStepIndexAlreadyReached,
    briefingState.modifyBriefingType,
  ]);

  const returnToReview = () => {
    navigate(`${briefingRoute}/${branch}/review`);
  };

  return (
    <div className="z-10 pb-10">
      <div className="flex mt-16 space-x-1">
        {stepIndex < steps.length - 1 && (
          <Button
            label={t('linksOrButtons:next')}
            disabled={isIncomplete(
              mandatoryFields[step],
              isValid,
              briefingState,
            )}
            onClick={onNext}
          />
        )}
        {stepIndex === steps.length - 1 && (
          <Button
            label={t('linksOrButtons:sendBriefing')}
            disabled={isIncomplete(
              mandatoryFields[step],
              isValid,
              briefingState,
            )}
            onClick={onSendBriefing}
          />
        )}

        {branch &&
          maxStepIndexAlreadyReached ===
            getStepsByBranchByMarket(
              countryMarketConfig?.eLabMarketSlug,
              briefingState.modifyBriefingType,
            )[branch].indexOf('review') &&
          step !== 'review' && (
            <Button
              label={t('linksOrButtons:backToReview')}
              variant="secondary"
              disabled={isIncomplete(
                mandatoryFields[step],
                isValid,
                briefingState,
              )}
              onClick={returnToReview}
            />
          )}
      </div>
    </div>
  );
};
